import type { CarouselItem } from "@/lib/api";

export type PublishPlatformId = "ig" | "fb";

export type PublishPlatformUiState = "live" | "missing" | "error" | "publishing" | "skipped";

export interface PublishPlatformStatus {
  id: PublishPlatformId;
  label: string;
  live: boolean;
  permalink?: string;
  mediaId?: string;
  uiState: PublishPlatformUiState;
  detail?: string;
}

/** Gdy Graph nie zwróci permalink_url, post i tak da się otworzyć po id. */
export function fbPermalinkFallback(postId?: string | null): string {
  const id = String(postId || "").trim();
  if (!id) return "";
  return `https://www.facebook.com/${id}`;
}

export function platformPostHref(
  platform: PublishPlatformId,
  item?: CarouselItem | null,
): string {
  if (platform === "ig") {
    return String(item?.ig_permalink || "").trim();
  }
  const permalink = String(item?.fb_permalink || "").trim();
  if (permalink) return permalink;
  return fbPermalinkFallback(item?.fb_post_id);
}

function stepState(item: CarouselItem | null | undefined, stepId: string): string {
  const steps = item?.publish_progress?.steps;
  if (!Array.isArray(steps)) return "";
  const row = steps.find((s) => s.id === stepId);
  return String(row?.state || "");
}

function lastRunFor(item: CarouselItem | null | undefined, platform: PublishPlatformId) {
  const runs = item?.publish_runs;
  if (!Array.isArray(runs)) return undefined;
  return runs.find((run) => {
    const ch = String(run.channel || "").toLowerCase();
    if (platform === "ig") return ch === "ig" || ch === "both";
    return ch === "fb" || ch === "both";
  });
}

export function hasIgPublished(item?: CarouselItem | null): boolean {
  return Boolean(item?.ig_permalink || item?.ig_media_id);
}

export function hasFbPublished(item?: CarouselItem | null): boolean {
  return Boolean(item?.fb_post_id || item?.fb_permalink);
}

export function publishPlatformStatus(
  item: CarouselItem | null | undefined,
  platform: PublishPlatformId,
): PublishPlatformStatus {
  const publishing =
    item?.status === "publishing" || item?.publish_progress?.phase === "publishing";
  const progressChannel = String(item?.publish_progress?.channel || "").toLowerCase();
  const live = platform === "ig" ? hasIgPublished(item) : hasFbPublished(item);
  const permalink = platformPostHref(platform, item) || undefined;
  const mediaId = platform === "ig" ? item?.ig_media_id : item?.fb_post_id;
  const lastRun = lastRunFor(item, platform);
  const igStepErr =
    stepState(item, "ig_publish") === "error" || stepState(item, "ig_slides") === "error";
  const fbStepErr = stepState(item, "fb_post") === "error" || stepState(item, "fb_photos") === "error";
  const stepErr = platform === "ig" ? igStepErr : fbStepErr;
  const publishError = String(item?.publish_error || "").trim();

  let uiState: PublishPlatformUiState = "missing";
  let detail = "";

  if (publishing && (progressChannel === platform || progressChannel === "both")) {
    uiState = "publishing";
    detail = String(item?.publish_progress?.detail || "");
  } else if (live) {
    uiState = "live";
  } else if (stepErr) {
    uiState = "error";
    detail =
      String(
        item?.publish_progress?.steps?.find((s) => s.state === "error")?.detail || publishError,
      ) || publishError;
  } else if (publishError && !live && lastRun && !lastRun.ok) {
    uiState = "error";
    detail = String(lastRun.error || publishError);
  } else if (
    publishing &&
    progressChannel !== platform &&
    progressChannel !== "both" &&
    (platform === "ig" ? progressChannel === "fb" : progressChannel === "ig")
  ) {
    uiState = "skipped";
  }

  return {
    id: platform,
    label: platform === "ig" ? "Instagram" : "Facebook",
    live,
    permalink: permalink || undefined,
    mediaId: mediaId || undefined,
    uiState,
    detail,
  };
}

export function publishStateBadge(uiState: PublishPlatformUiState): { text: string; className: string } {
  switch (uiState) {
    case "live":
      return { text: "Opublikowane", className: "bg-emerald-100 text-emerald-800" };
    case "publishing":
      return { text: "Publikuję…", className: "bg-brand/15 text-brand" };
    case "error":
      return { text: "Błąd", className: "bg-destructive/15 text-destructive" };
    case "skipped":
      return { text: "Pominięte", className: "bg-muted text-muted-foreground" };
    default:
      return { text: "Nie opublikowane", className: "bg-amber-100 text-amber-900" };
  }
}

export function resolvePublishChannel(ig: boolean, fb: boolean): "ig" | "fb" | "both" | null {
  if (ig && fb) return "both";
  if (ig) return "ig";
  if (fb) return "fb";
  return null;
}
